/* eslint-disable camelcase */
import request, { Options, Response } from 'request-dot-js'
import { Step } from 'prosemirror-transform'

import {
  type AccessIndex,
  type AccessRecord,
  type ArticleFlat,
  type Auth,
  type AnonymousUser,
  type CategoryDetail,
  type Channel,
  type ChannelInfo,
  type ChannelMember,
  type CompanyDetail,
  type CompanyMember,
  type Doc,
  type EventType,
  type MemberProfile,
  type Permission,
  type Profile,
  type SettingsGuest,
  type SettingsMember,
  type ShareUser,
  type SocialNetwork,
  type Spotlight,
  type UnmergedBranch,
  type Workspace,
  type WorkspaceSettings,
  type HandbookV2,
  type AlmanacFile,
} from 'types'
import store from 'redux/store'
import * as Actions from 'redux/actions'
import { REACT_APP_API_URL } from 'env'
import { getIpAddress } from 'utils/ipAddress'

// @ts-ignore
import runtimeEnv from '@mars/heroku-js-runtime-env'
import { reportError } from 'utils/error'
import { getEntityIdFromCurrentUrl } from 'utils/urlStoreUtils'
import { SessionService } from './services/SessionService'
import { getUniqueIdentifier } from 'utils/sessionStorage'
import qs from 'qs'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { getHandbookPassword } from 'hooks/useHandbookPasswords'
import { responsiveDesignLogic } from '../hooks/useResponsiveDesign'

export type GetterFunction<T = any> = () => Promise<Response<T>>

export const ApiUrl = runtimeEnv().REACT_APP_API_URL || REACT_APP_API_URL

// TODO: These types should be enforced
export const sendToApi = async <T = any, ET = any>(url: string, options: Options = {}, reduxStore = store) => {
  const { isRelative = true, headers = {}, ...rest } = options
  const retry = !options.method || options.method.toUpperCase() === 'GET' ? { retries: 4, delay: 2000 } : undefined

  const {
    auth,
    headers: stateHeaders,
    anonymousUser,
    unregisteredUser,
    workspace,
    articleMeta,
    articlePassword,
    handbook,
  } = reduxStore.getState()

  const entityId = getEntityIdFromCurrentUrl()
  const handbookPassword = getHandbookPassword(handbook?.key || entityId || undefined)

  const sessionHeaders = { 'x-session-uuid': getUniqueIdentifier() }

  let authHeaders = {}
  const articlePasswordId = entityId && articlePassword[entityId]
  const passwordHeader = articlePasswordId || handbookPassword

  if (passwordHeader) {
    authHeaders = {
      ...authHeaders,
      'X-Article-Password': passwordHeader,
    }
  }

  if (auth) {
    authHeaders = {
      ...authHeaders,
      ...{
        'x-superadmin-workspace-id':
          auth.superAdminMode === workspace.currentWorkspaceId ? workspace.currentWorkspaceId : null,
      },
      'X-Workspace-Id': workspace?.currentWorkspaceId,
      ...stateHeaders,
    }
  } else if (anonymousUser) {
    authHeaders = {
      ...authHeaders,
      'X-Workspace-Id': articleMeta[Object.keys(articleMeta)[0]]?.company_id,
      'X-Anonymous-User-Uuid': anonymousUser.uuid,
      ...stateHeaders,
    }
  } else if (unregisteredUser) {
    authHeaders = {
      ...authHeaders,
      'X-Workspace-Id': articleMeta[Object.keys(articleMeta)[0]]?.company_id,
      ...unregisteredUser.headers,
    }
  }

  const res = await request<T, ET>(isRelative ? `${ApiUrl}${url}` : url, {
    ...rest,
    headers: { ...headers, ...authHeaders, ...sessionHeaders },
    retry,
  })

  if (res.type === 'error' && res.status >= 500) {
    reportError(new Error('API client received error response'), {
      severity: 'warning',
      metaData: {
        info: `error: ${JSON.stringify(res.data)}, status: ${res.status}, status text: ${res.statusText}, url: ${
          res.url
        }`,
      },
    })
  }

  if (auth && res.type === 'error' && res.status === 401) SessionService.handleUnauthorized(res)

  return res
}

export type LoginData = { data: Auth }
export type ArticleFlatData = { article: ArticleFlat }
export type CategoriesData = { categories: CategoryDetail[] }
export type CategoriesDetailData = { category: CategoryDetail }
export type ProfileData = { user: Profile }
export type InvititationLinkData = { invitation_link: string }
export type ForgotPasswordData = { message: string; success: boolean }
export type WorkspaceMemberData = CompanyMember[]
export type CompanyDetailData = { company: CompanyDetail }
export type AccessData = { accesses: AccessIndex }
export type WorkspaceData = { company: WorkspaceSettings }
export type ScimTokenData = { token: string }
export type MembersData = {
  data: {
    admin_email: string
    admin: boolean
    members: SettingsMember[]
    paying: boolean
    user_count: number
    early_adopter: boolean
    customer_id: string | null
  }
}
export type GuestsData = {
  guests: SettingsGuest[]
}
export enum GuestRemovalType {
  simple = 'no_unmerged_branches',
  transferOwnership = 'transfer_ownership',
  deleteBranches = 'delete_branches',
}
export type PasswordResetData = {}
export type JobSearchTagsData = {
  experience_in_companies: string[]
  schools: string[]
  skills: string[]
  search_job_in_locations: string[]
  searched_roles: string[]
}

export type GetterResponse<T> = Response<T> | undefined

export interface AddMemberOpts {
  email: string
  role: 'User' | 'Admin'
  workspaceKey: string
  message?: string
  url_invitation?: boolean
  inviteReason?: string
}

// @TODO cleanup v1 https://www.pivotaltracker.com/story/show/171982480
export const nonV1api = () => {
  let apiUrl = runtimeEnv().REACT_APP_API_URL || REACT_APP_API_URL
  if (apiUrl.slice(-3) === '/v1') {
    apiUrl = apiUrl.slice(0, -3)
  }
  return apiUrl
}

const client = {
  // ACCOUNT ENDPOINTS
  // the optional domain is validated against workspace domains in the backend, login fails if it doesn't exist
  login: async (email: string, password: string, domain?: string) => {
    return sendToApi<LoginData>(`${nonV1api()}/users/sign_in`, {
      method: 'POST',
      body: { domain, user: { email, password } },
      isRelative: false,
    })
  },

  samlLogin: (email: string, redirect_to: string) => {
    return sendToApi(`${nonV1api()}/saml?email=${email}&redirect_url=${redirect_to}`, {
      method: 'GET',
      isRelative: false,
    })
  },
  impersonateUser: async (userId: number) => sendToApi<LoginData>(`/users/impersonate/${userId}`, { method: 'POST' }),

  updateUser: async (data: {}) => {
    return sendToApi<{ user: Auth }>(`/users`, { method: 'PUT', body: { data } })
  },

  resetPassword: async (email: string, redirect_url: string) => {
    return sendToApi<ForgotPasswordData>(`${nonV1api()}/users/password`, {
      method: 'POST',
      body: { email, redirect_url },
      isRelative: false,
    })
  },

  getAuthInfo: async (options: Options = {}) => {
    return sendToApi<{ user: Auth; anonymous_user?: null } | { anonymous_user: AnonymousUser; user?: null }>(
      `/users/self`,
      options,
    )
  },

  createUserExperimentImpression: async (featureFlagName: string) => {
    return sendToApi(`/user_experiments`, {
      method: 'POST',
      body: {
        feature_flag_name: featureFlagName,
      },
    })
  },

  // ONBOARDING ENDPOINTS
  sendInvites: async (emails: string[], workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/onboarding/invite`, {
      method: 'POST',
      body: { emails },
    })
  },

  // SETTINGS ENDPOINTS
  getWorkspaceSettings: async (workspaceKey: string) => {
    return sendToApi<WorkspaceData>(`/w/${workspaceKey}/settings/workspace`, {
      params: { include: ['version_control_mode.metadata'] },
    })
  },

  regenerateInvitationToken: async (workspaceKey: string) => {
    return sendToApi<{ invitation_token: string }>(`/w/${workspaceKey}/regenerate_invitation_token`, { method: 'POST' })
  },

  updateWorkspaceSettings: async (body: {}, workspaceKey: string) => {
    return sendToApi<WorkspaceData>(`/w/${workspaceKey}/settings/workspace`, {
      method: 'PUT',
      body,
      params: { include: ['version_control_mode.metadata'] },
    })
  },

  getWorkspaceMembers: async (workspaceKey: string) => {
    return sendToApi<MembersData>(`/w/${workspaceKey}/settings/members`)
  },

  updateWorkspaceMemberProfile: async (workspaceId: number, memberId: number, profile: Partial<MemberProfile>) => {
    return sendToApi<MemberProfile>(`/w/${workspaceId}/members/${memberId}`, { method: 'PUT', body: { data: profile } })
  },

  getWorkspaceGuests: async (workspaceKey: string) => {
    return sendToApi<GuestsData>(`/w/${workspaceKey}/settings/guests`)
  },

  deactivateWorkspace: async (workspaceKey: string) => {
    return sendToApi<void>(`/w/${workspaceKey}/deactivate`, { method: 'DELETE' })
  },

  addMember: async ({
    email,
    role,
    workspaceKey,
    inviteReason,
    message = '',
    url_invitation = false,
  }: AddMemberOpts) => {
    return sendToApi(`/w/${workspaceKey}/settings/members`, {
      method: 'POST',
      body: { role, email, url_invitation, message, invite_reason: inviteReason },
    })
  },

  upgradeGuestToMember: async (workspaceKey: string, guestId: number) => {
    return sendToApi<{ member: SettingsMember }>(`/w/${workspaceKey}/settings/guests/${guestId}/upgrade_to_member`, {
      method: 'PUT',
    })
  },

  removeGuestFromWorkspace: async (workspaceKey: string, guestId: number, removalType: GuestRemovalType) => {
    return sendToApi<{ member: SettingsMember }>(
      `/w/${workspaceKey}/settings/guests/${guestId}/remove_from_workspace?removal_type=${removalType}`,
      {
        method: 'DELETE',
      },
    )
  },

  getUnmergedBranchesForGuest: async (workspaceKey: string, guestId: number) => {
    return sendToApi<{ unmerged_branches: UnmergedBranch[] }>(
      `/w/${workspaceKey}/settings/guests/${guestId}/unmerged_branches`,
      {
        method: 'GET',
      },
    )
  },

  updateMemberRole: async (id: number, role: 'User' | 'Admin' | 'SuperAdmin', workspaceKey: string) => {
    return sendToApi<MembersData>(`/w/${workspaceKey}/settings/members/${id}`, { method: 'PUT', body: { role } })
  },

  deleteMember: async (id: number, workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/settings/members/${id}`, { method: 'DELETE' })
  },

  resendInvitation: async (id: number, workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/settings/members/${id}/resend_invitation`, { method: 'PUT' })
  },

  reactivateMember: async (id: number, admin: boolean, workspaceKey: string) => {
    return sendToApi(`/w/${workspaceKey}/settings/members/${id}/reactivate`, {
      method: 'PUT',
      body: { admin },
    })
  },

  refreshScimToken: async () => {
    return sendToApi<ScimTokenData>(`${nonV1api()}/scim/tokens`, {
      method: 'POST',
      isRelative: false,
    })
  },

  // OTHER ENDPOINTS
  getUploadUrl: async (body: {}) => {
    return sendToApi<{ url: string; error: string }>('/files/upload_url', { method: 'POST', body })
  },

  createArticle: (
    body: {
      project?: number | null
      title: string
      header_emoji?: string | null
      external_link?: string
      external_file_icon?: string
      stub?: boolean
      schema_version?: number
      channel?: ChannelType
    },
    workspace_key: string,
    homepageDoc?: boolean,
    channel_id?: number,
    steps?: Step[],
    createArticleViewEvent?: boolean,
    media_object_ids?: number[],
    markdown?: string,
    post_at?: string,
  ) => {
    return sendToApi<ArticleFlatData>('/articles', {
      method: 'POST',
      body: {
        article: body,
        homepage_doc: homepageDoc,
        create_article_view_event: createArticleViewEvent,
        channel_id,
        workspace_id: workspace_key,
        steps: steps?.map((step) => step.toJSON()),
        media_object_ids,
        markdown,
        post_at,
      },
    })
  },

  deleteArticle: async (id: string) => {
    return sendToApi(`/articles/${id}`, { method: 'DELETE' })
  },

  postArticleAccesses: async (
    articleId: string,
    member_accesses: {
      accessor_type: string
      accessor?: { id: number }
      permission: AccessRecord<any>['permission']
    }[],
    email_accesses: { email: string; permission: AccessRecord<any>['permission']; add_member?: boolean }[],
    new_sheet: boolean = false,
    html_message?: string,
    subject?: string,
    handbook: HandbookV2 | undefined = undefined,
    didUserEditInviteMessage: boolean = false,
  ) => {
    const body = {
      member_accesses,
      email_accesses,
      new_sheet,
      ...(handbook ? { handbook_key: handbook.key } : {}),
      html_message,
      subject,
      default_message: !didUserEditInviteMessage,
    }

    if (new_sheet) {
      return sendToApi<AccessData>(`/articles/${articleId}/accesses/create_from_new_sheet`, { method: 'POST', body })
    }

    return sendToApi<AccessData>(`/articles/${articleId}/accesses`, { method: 'POST', body })
  },

  updateArticleAccess: async (id: number, body: {}) => {
    return sendToApi(`/accesses/${id}`, { method: 'PUT', body })
  },

  deleteArticleAccess: async (id: number, removeReadReceipt: boolean) => {
    return sendToApi(`/accesses/${id}`, {
      method: 'DELETE',
      body: {
        remove_read_receipt: removeReadReceipt,
      },
    })
  },

  deleteArticlesAccesses: async (id: number | undefined, article_ids: string[]) => {
    return sendToApi(`/accesses/remove_multi_from_channel/${id}`, {
      method: 'DELETE',
      body: { article_ids },
    })
  },

  leaveArticle: async (id: string) => {
    return sendToApi(`/articles/${id}/accesses/leave`, { method: 'DELETE' })
  },

  usersWithAccess: async (id: string, handbook: HandbookV2 | undefined | null = undefined) => {
    return sendToApi<{ users: ShareUser[] }>(
      `/articles/${id}/accesses/users_with_access${handbook ? '?handbook=' + handbook.key : ''}`,
    )
  },

  usersWithEditAccess: async (id: string) => {
    return sendToApi<{ users: SettingsMember[] }>(`/articles/${id}/accesses/users_with_edit_access`)
  },

  updateUserProfile: async (body: {}) => {
    return sendToApi<ProfileData>(`/profile`, { method: 'PUT', body: { profile: body } })
  },

  getWorkspaceUsers: async (workspaceKey: string) => {
    return sendToApi<WorkspaceMemberData>(`/w/${workspaceKey}/members`)
  },

  postSocialShare: async (articleId: string, network: SocialNetwork) => {
    return sendToApi(`/articles/${articleId}/social_share`, { method: 'POST', body: { network } })
  },

  copyArticles: async (article_ids: string[], destination_workspace_id: string, workspace_key: string) => {
    return sendToApi<{ success: string[]; errors: string[] }>(`/w/${workspace_key}/copies`, {
      method: 'POST',
      body: { article_ids, destination_workspace_id },
    })
  },

  getCategories: async () => {
    return sendToApi<CategoriesData>('/categories')
  },

  getCategoriesForCreateFromTemplateModal: async () => {
    const resp = await sendToApi<CategoriesData>('/categories?create_from_template=true')
    if (resp.type !== 'success') return null
    return resp.data
  },

  getCategory: async (categoryId: number) => {
    return sendToApi<CategoriesDetailData>(`/categories/${categoryId}`)
  },

  // Use when you just need basic user details
  getUserCard: async (userId: number) => {
    return sendToApi<ProfileData>(`/users/${userId}/card`)
  },

  // Only use for full user profile
  getUserProfile: async (userId: number) => {
    const response = await sendToApi<ProfileData>(`/users/${userId}`)

    if (response.type === 'success') {
      const {
        data: {
          user: { following_id: followingId, id },
        },
      } = response

      if (followingId) {
        store.dispatch(Actions.followeeAdd({ id, followingId }))
      }
    }

    return response
  },

  getMemberProfile: async (workspaceId: number | string, memberId: number) => {
    return sendToApi<MemberProfile>(`/w/${workspaceId}/members/${memberId}`)
  },

  getMemberArticles: async (workspaceId: number | string, memberId: number, items: number = 3) => {
    return sendToApi<Doc[]>(`/w/${workspaceId}/members/${memberId}/articles?items=${items}`)
  },

  getInvitationLink: async (workspaceKey: string) => {
    return sendToApi<InvititationLinkData>(`/w/${workspaceKey}/invitation_link`)
  },

  postUserCompany: async (
    name: string,
    company_size?: number,
    team_type?: number,
    logo_url?: string,
    is_freemium?: boolean,
    is_virtual_marketer?: boolean,
    is_campaigns?: boolean,
    subscription_coupon_id?: string,
  ) => {
    return sendToApi<CompanyDetailData>(`/w`, {
      method: 'POST',
      body: {
        company: {
          name,
          company_size,
          team_type,
          logo_url,
          is_freemium,
          is_virtual_marketer,
          is_campaigns,
          subscription_coupon_id,
        },
      },
    })
  },

  getUserWorkspaces: async () => {
    return sendToApi<{ workspaces: Workspace[] }>('/w')
  },

  updateUserPassword: async (body: { profile: { old_password: string; new_password: string } }) => {
    return sendToApi<PasswordResetData>('/password', { method: 'PUT', body })
  },

  resetUserPassword: async (body: { reset_password_token: string; password: string }) => {
    return sendToApi<PasswordResetData>('/password-reset', { method: 'PUT', body })
  },

  createChannel: async (
    workspaceKey: string,
    params: {
      accessibility?: string
      description?: string | null
      name: string
      color?: string | null
      emoji?: string | null
    },
  ) => {
    return sendToApi<{ channel: ChannelInfo }>(`/w/${workspaceKey}/channels`, {
      method: 'POST',
      body: { channel: params },
    })
  },

  getWorkspaceChannels: async (workspaceKey: string, includeOpenGroups: boolean = false) => {
    return sendToApi<{ channels: ChannelInfo[] }>(`/w/${workspaceKey}/channels`, {
      params: { include_open_groups: !!includeOpenGroups }, // This will include all non-private groups if true
    })
  },

  getChannelInfo: async (id: number) => {
    return sendToApi<{ channel: ChannelInfo }>(`/channels/${id}`)
  },

  getChannelDocs: async (id: number) => {
    return sendToApi<{ articles: Doc[] }>(`/channels/${id}/all_articles`)
  },

  getAvailableChannels: async (workspaceKey: string) => {
    return sendToApi<{ channels: ChannelInfo[] }>(`/w/${workspaceKey}/available_channels`)
  },

  deleteChannel: async (id: number) => {
    return sendToApi<void>(`/channels/${id}`, { method: 'DELETE' })
  },

  getChannelNonMembers: async (id: number) => {
    return sendToApi<{ non_members: ShareUser[] }>(`/channels/${id}/non_members`)
  },

  addChannelMembers: async (id: number, newUsers: string[]) => {
    return sendToApi<{ channel_memberships: ChannelMember[] }>(`/channels/${id}/channel_memberships`, {
      method: 'POST',
      body: { channel_memberships: { emails: newUsers } },
    })
  },

  joinChannel: async (channelId: number, userId: number) => {
    return sendToApi<{ channels: Channel[] }>(`/channels/${channelId}/join`, {
      method: 'POST',
      body: { user_id: userId },
    })
  },

  acceptChannelRequest: async (requestId: number, token: string) => {
    return sendToApi<{ channel_memberships: ChannelMember[] }>(`/channel_join_requests/${requestId}/accept`, {
      method: 'POST',
      body: { token },
    })
  },

  denyChannelRequest: async (requestId: number) => {
    return sendToApi(`/channel_join_requests/${requestId}/deny`, { method: 'DELETE' })
  },

  requestDocAccess: async (articleId: string, permission: Permission) => {
    return sendToApi<void>(`/articles/${articleId}/access_requests`, {
      method: 'POST',
      body: { permission: permission },
    })
  },

  acceptAccessRequest: async (token: string, permission: Permission, addToWorkspace: boolean = true) => {
    return sendToApi<void>(`/access_requests/${token}/accept`, {
      method: 'PUT',
      body: { permission, add_to_workspace: addToWorkspace },
    })
  },

  trackEvent: async (
    eventName: EventType,
    properties: { [key: string]: string | number | boolean | null },
    options?: { headers?: { authorization: string }; getIpAddress?: boolean },
  ) => {
    const { auth } = store.getState()

    const { isIOSNative } = responsiveDesignLogic()
    properties = {
      is_ios_native: isIOSNative,
      ...properties,
    }

    if (options && options.getIpAddress && !properties.ip_address) {
      const ip_address = await getIpAddress()
      properties = {
        ...properties,
        ip_address,
      }
    }
    return sendToApi<{ message: string }>('/events', {
      method: 'POST',
      body: {
        event_name: eventName,
        workspace_id: auth && auth.company_id,
        article_id: getEntityIdFromCurrentUrl(),
        properties,
      },
      headers: (options && options.headers) || {},
    })
  },

  trackCampaignEvent: async (urlPathnameWithParams: string) => {
    const { auth } = store.getState()

    return sendToApi<{ message: string }>('/campaigns/event', {
      method: 'POST',
      body: {
        workspace_id: auth && auth.company_id,
        article_id: getEntityIdFromCurrentUrl(),
        properties: {
          ip_address: await getIpAddress(),
          url: urlPathnameWithParams,
        },
      },
    })
  },

  makeArticlePublic: async (id: string, category_id: number) => {
    return sendToApi<ArticleFlatData>(`/articles/${id}/make_public`, { method: 'PUT', body: { category_id } })
  },

  makeArticlePrivate: async (id: string) => {
    return sendToApi<ArticleFlatData>(`/articles/${id}/make_private`, { method: 'PUT' })
  },

  getJobTags: async () => {
    return sendToApi<JobSearchTagsData>('/job_search_tags')
  },

  getSpotlight: async () => {
    return sendToApi<{ spotlight: Spotlight | {} }>('/spotlights')
  },

  getUserAlgoliaKey: async () => {
    return sendToApi<{ algolia_api_key: string }>(`/users/algolia_api_key`)
  },
  acceptChanges: async (articleId: string, layerId: string, uuids: string[]) => {
    return sendToApi(`/articles/${articleId}/layers/${layerId}/changes/bulk_accept`, {
      method: 'POST',
      body: { uuids },
    })
  },
  getRecentDocs: async ({
    workspaceId,
    items = 4,
    withActiveReviews,
    permissions,
    skipProjects,
  }: {
    workspaceId: number
    items: number
    withActiveReviews?: boolean
    permissions?: Permission[]
    skipProjects?: boolean
  }) => {
    return sendToApi(`/w/${workspaceId}/files_views/recent`, {
      method: 'GET',
      params: {
        items,
        with_active_reviews: withActiveReviews,
        permissions: permissions?.join(','),
        skip_projects: skipProjects,
      },
    })
  },
  // TODO: remove once new home page replaces old one GO-1992
  getMostFrequentlyViewed: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/files_views/frequently_viewed_articles`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  // TODO: remove once new home page replaces old one GO-1992
  getRecentlyUpdated: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/files_views/recently_updated`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  getMostFrequentlyViewedDocs: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/frequently_viewed_articles`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  getMostFrequentlyViewedProjects: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/frequently_viewed_projects`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  getMostRecentlyViewedDocs: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/recently_viewed_articles`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  getMostRecentlyViewedProjects: async (params: { workspaceId: number; items: number; page: number }) => {
    return sendToApi(`/w/${params.workspaceId}/recently_viewed_projects`, {
      params,
      stringify: (object) => qs.stringify(object, { arrayFormat: 'brackets' }),
    })
  },
  pinFile: async (id: string) => {
    return sendToApi(`/almanac_files/${id}/file_pin/`, { method: 'POST' })
  },
  unpinFile: async (id: string) => {
    return sendToApi(`/almanac_files/${id}/file_pin/`, { method: 'DELETE' })
  },
  insertPinAt: async (id: string, position: number) => {
    return sendToApi<{ pinned_files: AlmanacFile[] }>(`/almanac_files/${id}/file_pin/`, {
      body: { insert_at: position },
      method: 'PUT',
    })
  },
}
export default client
